/// <amd-module name="Comments/Item/Components/CommentItem" />
import { CommentReactionType } from "Comments/Common/Contracts";
import { UsageTelemetryHelper } from "Comments/Common/Telemetry";
import { ICommentEditorProps } from "Comments/Editor/Components/CommentEditor.Props";
import { ICommentItemProps } from "Comments/Item/Components/CommentItem.Props";
import { CommentViewer } from "Comments/Viewer/Components/CommentViewer";
import * as React from "react";
import { shallowCompare } from "VSS/Core/Util/Object";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Components, css, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";

const CommentEditorType = "comment-editor";

export interface ICommentItemState {
    isEditorLoaded: boolean;
}

/**
 * Composite component for comment viewer and editor.
 */
export class CommentItem extends VssComponent<ICommentItemProps, ICommentItemState> {
    private _contributionService: IVssContributionService;
    private _usageTelemetry: UsageTelemetryHelper;
    private _editButtonRef: React.RefObject<Button> = React.createRef<Button>();

    constructor(props: ICommentItemProps, context: IVssComponentContext) {
        super(props, context);

        this.state = { isEditorLoaded: !!Components.get(CommentEditorType) };

        this._usageTelemetry = new UsageTelemetryHelper(props.artifactInfo, context.pageContext);
    }

    public render(): JSX.Element {
        const { compactMode } = this.props;
        return compactMode ? this._renderCompactMode() : this._renderFullMode();
    }

    public componentDidMount(): void {
        this._fetchEditorIfNeeded();
    }

    public componentDidUpdate(prevProps: ICommentItemProps, prevState: ICommentItemState): void {
        super.componentDidUpdate(prevProps, prevState);

        if (prevProps.isEditing && !this.props.isEditing) {
            if (isFirefox()) {
                // Delay focus since Firefox will also fire click event on the edit button if user press space bar on the Update or Cancel button
                setTimeout(() => this._resetFocusToEditButton(), 200);
                return;
            }

            this._resetFocusToEditButton();
        }

        this._fetchEditorIfNeeded();
    }

    public shouldComponentUpdate(newProps: ICommentItemProps, newState: ICommentItemState): boolean {
        return !shallowCompare(newProps, this.props) || !shallowCompare(newState, this.state);
    }

    private _resetFocusToEditButton(): void {
        this._editButtonRef.current && this._editButtonRef.current.focus();
    }

    private _renderCompactMode(): JSX.Element {
        const { className, bodyClassName } = this.props;
        return <div className={css("comment-item", "compact-mode", className, bodyClassName)}>{this._renderBody()}</div>;
    }

    private _renderFullMode(): JSX.Element {
        const { comment, className, bodyClassName, onRenderPersona } = this.props;
        const identity = comment.createdOnBehalfOf || comment.createdBy;
        return (
            <div className={css("comment-item flex-row", className)}>
                <div className={"comment-item-left"}>{onRenderPersona(identity)}</div>
                <div className={css("comment-item-right flex-grow", bodyClassName)}>{this._renderBody()}</div>
            </div>
        );
    }

    private _renderBody(): JSX.Element {
        const {
            comment,
            editorClassName,
            isEditing,
            onChange,
            onDirtyChange,
            onRenderEditor,
            onEditorMounted,
            onRenderPreview,
            uploadImageHandler
        } = this.props;
        const { isEditorLoaded } = this.state;

        if (isEditing && isEditorLoaded) {
            return (
                <WrappedComponent<ICommentEditorProps>
                    wrappedType={CommentEditorType}
                    wrappedProps={{
                        comment,
                        onUpdate: this._onUpdate,
                        onCancelEdit: this._onCancelEdit,
                        onRenderEditor,
                        onRenderPreview,
                        onDirtyChange,
                        onEditorMounted,
                        onKeepDraft: this._onKeepDraft,
                        onChange,
                        uploadImageHandler,
                        className: editorClassName
                    }}
                />
            );
        }

        return this._renderViewer();
    }

    private _renderViewer = (): JSX.Element => {
        const {
            readonly,
            canEdit,
            formatDisabled,
            comment,
            baseUrl,
            viewerClassName,
            viewerContentClassName,
            onPostRenderContent,
            compactMode,
            getPermalink,
            isReactionsEnabled,
            onGetRecentlyReactedUsers,
            reactionTypeToInfoMap,
            onRenderPersona,
            onRenderCommentBody,
            tooltipTimeFormat,
            currentDate,
            doNotHighlight
        } = this.props;

        return (
            <CommentViewer
                readonly={readonly}
                canEdit={canEdit}
                comment={comment}
                baseUrl={baseUrl}
                formatDisabled={formatDisabled}
                editButtonRef={this._editButtonRef}
                onStartEdit={this._onStartEdit}
                onConfirmDelete={this._onConfirmDelete}
                onMoreClick={this._onMoreClick}
                className={viewerClassName}
                contentClassName={viewerContentClassName}
                getPermalink={getPermalink}
                onPostRenderContent={onPostRenderContent}
                onRenderPersona={compactMode ? onRenderPersona : undefined}
                isReactionsEnabled={isReactionsEnabled}
                onAddReactionButtonClick={this._onAddReactionButtonClick}
                onAddReaction={this._onAddReaction}
                onDeleteReaction={this._onDeleteReaction}
                onGetRecentlyReactedUsers={onGetRecentlyReactedUsers}
                reactionTypeToInfoMap={reactionTypeToInfoMap}
                onRenderCommentBody={onRenderCommentBody}
                tooltipTimeFormat={tooltipTimeFormat}
                currentDate={currentDate}
                doNotHighlight={doNotHighlight}
            />
        );
    };

    private _onAddReactionButtonClick = (): void => {
        this._usageTelemetry.publishClickReactionSelectionButton();
    };

    private _onAddReaction = (commentId: number, reactionType: CommentReactionType, isAddedFromStatusBar: boolean): void => {
        const { onAddReaction } = this.props;
        onAddReaction && onAddReaction(commentId, reactionType, isAddedFromStatusBar);

        this._usageTelemetry.publishEngagedReaction(reactionType.toString(), isAddedFromStatusBar);
    };

    private _onDeleteReaction = (commentId: number, reactionType: CommentReactionType, isDeletedFromStatusBar: boolean): void => {
        const { onDeleteReaction } = this.props;
        onDeleteReaction && onDeleteReaction(commentId, reactionType, isDeletedFromStatusBar);

        this._usageTelemetry.publishDisEngagedReaction(reactionType.toString(), isDeletedFromStatusBar);
    };

    private _onCancelEdit = (commentId: number, isDirty: boolean): void => {
        const { onCancelEdit } = this.props;
        onCancelEdit(commentId, isDirty);

        this._usageTelemetry.publishEditCommentCancel(isDirty);
    };

    private _onStartEdit = (commentId: number): void => {
        const { onStartEdit } = this.props;
        onStartEdit(commentId);

        this._usageTelemetry.publishEditComment();
    };

    private _onConfirmDelete = (commentId: number, confirmed: boolean): void => {
        const { onConfirmDelete } = this.props;
        onConfirmDelete(commentId, confirmed);
    };

    private _onMoreClick = (commentId: number): void => {
        const { onMoreClick } = this.props;
        onMoreClick && onMoreClick(commentId);

        this._usageTelemetry.publishClickMoreButton();
    };

    private _onUpdate = (commentId: number, newValue: string, oldValue: string): void => {
        const { onUpdate, comment } = this.props;
        onUpdate(commentId, newValue, oldValue);

        this._usageTelemetry.publishEditCommentUpdate(oldValue.length, newValue.length, comment!.version);
    };

    private _onKeepDraft = (commentId: number, newValue: string): void => {
        const { onKeepDraft } = this.props;
        onKeepDraft && onKeepDraft(commentId, newValue);

        this._usageTelemetry.publishEditCommentKeepDraft();
    };

    // fetch the editor ourselves instead of using wrapped component, to have better control when the DOM is updated
    private _fetchEditorIfNeeded(): void {
        if (!this.props.isEditing || this.state.isEditorLoaded) {
            return;
        }

        const id = "ms.vss-comments.editor";
        this.trackPromise(
            this._getContributionService()
                .getContributionAsync(id)
                .then(() => this.setState({ isEditorLoaded: true }))
                .catch((e) => e && !e.canceled && this._usageTelemetry.traceError("_fetchEditorIfNeeded", e, { id }))
        );
    }

    private _getContributionService(): IVssContributionService {
        if (!this._contributionService) {
            this._contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        }

        return this._contributionService;
    }
}

VssComponent.register("comment-item", CommentItem);

function isFirefox(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /mozilla.*rv:([\d+.]+)/.test(userAgent) && userAgent.indexOf("trident") < 0;
}
